import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useLanguage } from '../context/LanguageContext';

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const location = useLocation();
  const { t, language } = useLanguage();

  const mainNavLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.loanPrograms'), path: '/loans' },
    { name: language === 'en' ? 'Recent Deals' : language === 'zh_CN' ? '成交案例' : '成交案例', path: '/deals' },
  ];

  const companyLinks = [
    { name: t('nav.howItWorks'), path: '/how-it-works' },
    { name: t('nav.about'), path: '/about' },
    { name: language === 'en' ? 'Blog' : '博客', path: '/blog' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  const companyLabel = language === 'en' ? 'Company' : language === 'zh_CN' ? '公司' : '公司';

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isCompanyActive = () => {
    return companyLinks.some(link => location.pathname.startsWith(link.path));
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 nav-glass border-b border-slate-200" data-testid="navbar">
      <div className="container-main">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <div className="flex flex-col items-start leading-none">
              <span className="font-heading !font-bold !text-2xl text-slate-900">   G  A  N  N</span>
              <div className="w-full h-px bg-amber-700 my-1"></div>
              <span className="font-heading text-xs font-semibold text-amber-700 tracking-[0.25em] uppercase">Private Lending</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {mainNavLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-body text-sm font-medium uppercase tracking-wider link-underline ${
                  isActive(link.path) ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid={`nav-link-${link.path.replace(/\//g, '') || 'home'}`}
              >
                {link.name}
              </Link>
            ))}

            {/* Company Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setCompanyDropdownOpen(true)}
              onMouseLeave={() => setCompanyDropdownOpen(false)}
            >
              <button
                className={`font-body text-sm font-medium uppercase tracking-wider flex items-center gap-1 ${
                  isCompanyActive() ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="company-dropdown-btn"
              >
                {companyLabel}
                <ChevronDown size={16} className={`transition-transform ${companyDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {companyDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-slate-200 shadow-lg py-2">
                  {companyLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`block px-4 py-2 font-body text-sm ${
                        isActive(link.path) 
                          ? 'text-slate-900 bg-slate-50' 
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                      }`}
                      data-testid={`dropdown-link-${link.path.replace(/\//g, '')}`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Apply Link */}
            <Link
              to="/apply"
              className={`font-body text-sm font-medium uppercase tracking-wider link-underline ${
                isActive('/apply') ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="nav-link-apply"
            >
              {language === 'en' ? 'Apply' : language === 'zh_CN' ? '申请' : '申請'}
            </Link>
          </div>

          {/* CTA Section */}
          <div className="hidden lg:flex items-center space-x-4">
            <LanguageSwitcher />
            <a
              href="tel:424-303-2130"
              className="flex items-center space-x-2 phone-link"
              data-testid="phone-link"
            >
              <Phone size={18} />
              <span className="font-body font-semibold">424-303-2130</span>
            </a>
            <Link to="/contact" data-testid="get-funded-btn">
              <Button className="btn-gold rounded-none">
                {t('nav.getFunded')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              className="p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-200" data-testid="mobile-menu">
            <div className="flex flex-col space-y-4">
              {mainNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-body text-sm font-medium uppercase tracking-wider py-2 ${
                    isActive(link.path) ? 'text-slate-900' : 'text-slate-600'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.path.replace(/\//g, '') || 'home'}`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Company Section in Mobile */}
              <div className="border-t border-slate-100 pt-2">
                <span className="font-body text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 block">
                  {companyLabel}
                </span>
                {companyLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`font-body text-sm font-medium uppercase tracking-wider py-2 pl-4 block ${
                      isActive(link.path) ? 'text-slate-900' : 'text-slate-600'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${link.path.replace(/\//g, '')}`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>

              {/* Apply Link */}
              <Link
                to="/apply"
                className={`font-body text-sm font-medium uppercase tracking-wider py-2 ${
                  isActive('/apply') ? 'text-slate-900' : 'text-slate-600'
                }`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-apply"
              >
                {language === 'en' ? 'Apply' : language === 'zh_CN' ? '申请' : '申請'}
              </Link>

              <div className="pt-4 border-t border-slate-200 space-y-4">
                <a
                  href="tel:424-303-2130"
                  className="flex items-center space-x-2 phone-link"
                  data-testid="mobile-phone-link"
                >
                  <Phone size={18} />
                  <span className="font-body font-semibold">424-303-2130</span>
                </a>
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="btn-gold rounded-none w-full" data-testid="mobile-get-funded-btn">
                    {t('nav.getFunded')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
